import { Badge } from "@/components/ui/baseUi/badge"
import { Button } from "@/components/ui/baseUi/button"
import { errorToastClassName, successToastClassName, appToast as toast } from "@/components/ui/baseUi/sonner"
import { Text } from "@/components/ui/baseUi/text"
import { dragAndDrop } from "@formkit/drag-and-drop/react"
import { HiddenPriceIcon } from "@images/svgIcons/essentials/hiddenPriceIcon"
import { SlashedEyeLineIcon } from "@images/svgIcons/slashedEyeLineIcon"
import { CoachOfferEntity } from "@modules/coachOffers/entities/coachOfferEntity"
import { CoachOfferType } from "@modules/coachOffers/entities/coachOfferTypes"
import { useCoachOffers, useUpdateCoachOffersOrder } from "@modules/coachOffers/hooks/useCoachOffers"
import { word } from "@modules/core/utils/i18n"
import { cn } from "@modules/lib/utils"
import { useSidebar } from "@modules/sidebar/hooks/useSidebar"
import { CircleIcon, CirclePileIcon, GripVertical, LoaderCircleIcon } from "lucide-react"
import { useEffect, useRef, useState } from "react"

export function CoachOfferOrderForm() {
  const { data: offers } = useCoachOffers()

  const [previousOffers, setPreviousOffers] = useState<CoachOfferEntity[]>(offers)
  const [visibleOrder, setVisibleOrder] = useState<string[]>(filterAndSortByDisplayOrder(offers, true).map((o) => o.id))
  const [hiddenOrder, setHiddenOrder] = useState<string[]>(filterAndSortByDisplayOrder(offers, false).map((o) => o.id))
  const { hideSidebar } = useSidebar()
  const { saveOrder, isPending } = useUpdateCoachOffersOrder()
  const visibleListRef = useRef<HTMLDivElement>(null)

  if (offers !== previousOffers) {
    setPreviousOffers(offers)
    setVisibleOrder(filterAndSortByDisplayOrder(offers, true).map((o) => o.id))
    setHiddenOrder(filterAndSortByDisplayOrder(offers, false).map((o) => o.id))
  }

  useEffect(() => {
    if (!visibleListRef.current) return
    dragAndDrop({
      parent: visibleListRef,
      state: [visibleOrder, setVisibleOrder],
      group: "coachOfferVisible",
    })
  }, [visibleOrder])

  const handleSaveOrder = async () => {
    try {
      await saveOrder(visibleOrder)
      toast.success(word("coach.offers.order.save.success"), { classNames: successToastClassName() })
      hideSidebar()
    } catch {
      toast.error(word("coach.offers.order.save.error"), { classNames: errorToastClassName() })
    }
  }

  const getVisibilityInfo = (offer: CoachOfferEntity) => {
    if (!offer.showcaseDisplay)
      return {
        text: word("box.offer.form.resume.showcaseDisplay"),
        icon: <SlashedEyeLineIcon color="currentColor" />,
      }
    if (!offer.priceDisplay)
      return {
        text: word("box.offer.form.resume.priceDisplay"),
        icon: <HiddenPriceIcon color="currentColor" />,
      }

    return { text: "", icon: null }
  }

  const getTypeIcon = (type: CoachOfferType) => {
    switch (type) {
      case CoachOfferType.Individual:
        return <CircleIcon size={14} />
      case CoachOfferType.Collective:
        return <CirclePileIcon size={14} />
    }
  }

  const renderList = ({ ids, hidden }: { ids: string[]; hidden: boolean }) => {
    if (ids.length === 0) {
      return (
        <div className="flex h-16 items-center justify-center border-t border-surface-border-gray-subtle">
          <Text fontStyle="BS">{word("coach.offers.order.list.empty")}</Text>
        </div>
      )
    }

    return ids.map((id) => {
      const offer = offers.find((o) => o.id === id)
      if (!offer) return null
      const visibilityInfo = getVisibilityInfo(offer)

      return (
        <div
          key={id}
          data-id={id}
          className={cn(
            "grid grid-cols-[auto_1fr_1fr] items-center gap-1 border-t border-surface-border-gray-subtle bg-surface-background px-2 py-4",
            hidden ? "cursor-default" : "cursor-grab",
          )}
        >
          <GripVertical
            size={18}
            color="currentColor"
            className={cn("mr-3", hidden ? "text-textOnSurface-disabled" : "text-textDefault-body")}
          />
          <div className="flex flex-col gap-1">
            <Text fontStyle="BM" className={cn(hidden ? "text-textOnSurface-disabled" : "text-textDefault-body")}>
              {offer.name}
            </Text>
            <Badge variant={hidden ? "neutralSubtle" : "primaryIntense"} size="sm">
              {getTypeIcon(offer.type)}
              {word(`coach.offers.type.${offer.type}`)}
            </Badge>
          </div>

          <div className="flex items-center gap-2 px-3 py-1">
            <div
              className={cn(
                "flex items-center",
                hidden ? "h-4 w-4 text-textOnSurface-disabled" : "h-4.5 w-4.5 text-textDefault-caption",
              )}
            >
              {visibilityInfo.icon}
            </div>
            <Text fontStyle="BS" className={hidden ? "text-textOnSurface-disabled" : "text-textDefault-caption"}>
              {visibilityInfo.text}
            </Text>
          </div>
        </div>
      )
    })
  }

  const renderSection = (type: "visible" | "hidden") => {
    const { title, subtitle, textColor, list, listRef } = {
      visible: {
        title: word("global.visibility.visible"),
        subtitle: word("coach.offers.order.section.visible.description"),
        textColor: "text-textDefault-heading",
        list: visibleOrder,
        listRef: visibleListRef,
      },
      hidden: {
        title: word("global.visibility.hidden"),
        subtitle: word("coach.offers.order.section.hidden.description"),
        textColor: "text-textOnSurface-disabled",
        list: hiddenOrder,
        listRef: null,
      },
    }[type]

    return (
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-1">
          <Text fontStyle="TM" className="text-textDefault-caption">
            {title}
          </Text>
          <Text fontStyle="BS" className="text-textDefault-caption">
            {subtitle}
          </Text>
        </div>

        <div className="flex flex-col gap-1">
          <div className="grid grid-cols-[auto_1fr_1fr] items-center gap-1 px-3">
            <div className="mr-3" />
            <Text fontStyle="BM" className={"ml-4 " + textColor}>
              {word("coach.offers.order.column.offer")}
            </Text>
            <Text fontStyle="BM" className={"ml-5 " + textColor}>
              {word("global.visibility.label")}
            </Text>
          </div>

          <div className="flex flex-col" ref={listRef}>
            {renderList({ ids: list, hidden: type === "hidden" })}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-6 p-8 pt-16">
      <div className="flex flex-col gap-1">
        <Text fontStyle="TL" className="text-textDefault-heading">
          {word("coach.offers.order.title")}
        </Text>
        <Text fontStyle="BM" className="text-textDefault-body">
          {word("coach.offers.order.description")}
        </Text>
      </div>

      {renderSection("visible")}

      {renderSection("hidden")}

      <div className="flex justify-between gap-5 py-3">
        <Button
          variant="outline"
          size="lg"
          className="flex-1"
          onClick={() => {
            hideSidebar()
          }}
        >
          {word("global.cancel")}
        </Button>

        <Button
          variant="primary"
          size="lg"
          className="flex-1"
          onClick={() => void handleSaveOrder()}
          disabled={isPending}
        >
          {word("global.update")}
          {isPending && <LoaderCircleIcon className="mr-2 animate-spin" />}
        </Button>
      </div>
    </div>
  )
}

const filterAndSortByDisplayOrder = (offers: CoachOfferEntity[], visible: boolean) => {
  const items = visible ? offers.filter((o) => o.showcaseDisplay) : offers.filter((o) => !o.showcaseDisplay)

  return [...items].sort((a, b) => {
    const ao = a.displayOrder ?? Number.MAX_SAFE_INTEGER
    const bo = b.displayOrder ?? Number.MAX_SAFE_INTEGER
    if (ao === bo) return a.name.localeCompare(b.name)
    return ao - bo
  })
}
